/**
 * Persistent location sharing service.
 *
 * Starts automatically when any order goes en_route and keeps running
 * in the background — survives page navigation, logout, and app
 * backgrounding (on native via BackgroundGeolocation).
 *
 * State is persisted to localStorage so the watcher can be re-established
 * when the app is re-opened.
 */

import { Capacitor, registerPlugin } from '@capacitor/core'
import { Geolocation } from '@capacitor/geolocation'
import { apiFetch } from '@/lib/api'

// ---------- Native plugin types ----------
interface BGLocation {
  latitude: number
  longitude: number
  accuracy: number
  bearing: number | null
  speed: number | null
  time: number | null
}

interface BackgroundGeolocationPlugin {
  addWatcher(
    options: {
      backgroundMessage?: string
      backgroundTitle?: string
      requestPermissions?: boolean
      stale?: boolean
      distanceFilter?: number
    },
    callback: (position: BGLocation | null, error?: any) => void,
  ): Promise<string>
  removeWatcher(options: { id: string }): Promise<void>
}

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>('BackgroundGeolocation')

// ---------- localStorage key ----------
const STORAGE_KEY = 'deeliver_location_sharing'

interface SharingState {
  driverId: string
  orderIds: string[]
  startedAt: number
}

// ---------- Singleton state ----------
let nativeWatchId: string | null = null
let webWatchId: string | null = null
let currentOrderIds: string[] = []
let currentDriverId: string | null = null
let running = false
const persistedOrderRows = new Set<string>()
const lastPersistedAtByOrder = new Map<string, number>()
const DB_PERSIST_INTERVAL_MS = 30_000

function getApiBaseUrl(): string {
  if (typeof window === 'undefined') return ''
  // NEXT_PUBLIC_ vars are inlined at build time
  return (process.env.NEXT_PUBLIC_API_BASE_URL || window.location.origin).replace(/\/$/, '')
}

// ---------- Persistence ----------
function saveState(driverId: string, orderIds: string[]) {
  try {
    const state: SharingState = { driverId, orderIds, startedAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state))
  } catch {}
}

function loadState(): SharingState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return null
    return JSON.parse(raw) as SharingState
  } catch {
    return null
  }
}

function clearState() {
  try { localStorage.removeItem(STORAGE_KEY) } catch {}
}

function persistLocation(orderId: string, lat: number, lng: number, acc: number, heading?: number | null, speed?: number | null) {
  if (!currentDriverId) return
  const now = Date.now()
  const lastPersistedAt = lastPersistedAtByOrder.get(orderId) ?? 0
  const shouldPersist = !persistedOrderRows.has(orderId) || now - lastPersistedAt >= DB_PERSIST_INTERVAL_MS
  const method = persistedOrderRows.has(orderId) ? 'PATCH' : 'POST'

  apiFetch('/api/location/update', {
    method,
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({
      orderId,
      driverId: currentDriverId,
      latitude: lat,
      longitude: lng,
      accuracy: acc,
      heading: heading ?? null,
      speed: speed ?? null,
      persist: shouldPersist,
    }),
  }).then(async (response) => {
    const result = await response.json().catch(() => null)
    if (response.ok && result?.persisted) {
      persistedOrderRows.add(orderId)
      lastPersistedAtByOrder.set(orderId, now)
    }
  }).catch((err) => {
    console.warn('[LocationService] failed to persist location', err?.message || err)
  })
}

function broadcast(lat: number, lng: number, acc: number, heading?: number | null, speed?: number | null) {
  if (!currentDriverId || currentOrderIds.length === 0) return
  currentOrderIds.forEach((oid) => persistLocation(oid, lat, lng, acc, heading, speed))
  console.log('[LocationService] persisted location for', currentOrderIds.length, 'orders')
}

// ---------- Public API ----------

/** Start (or update) location sharing for the given driver + orders. */
export async function startLocationSharing(driverId: string, orderIds: string[]) {
  if (typeof window === 'undefined') return
  console.log('[LocationService] start/sync requested', { driverId, orderIds, running, currentDriverId })
  if (orderIds.length === 0) {
    console.log('[LocationService] no en-route orders to share')
    if (running && currentDriverId === driverId) {
      await stopLocationSharing()
    }
    return
  }

  // If already running for this driver, sync to the exact active en-route set.
  if (running && currentDriverId === driverId) {
    if (orderIds.length === 0) {
      await stopLocationSharing()
      return
    }
    const newIds = orderIds.filter((id) => !currentOrderIds.includes(id))
    const removedIds = currentOrderIds.filter((id) => !orderIds.includes(id))
    currentOrderIds = Array.from(new Set(orderIds))
    console.log('[LocationService] synced active orders', { currentOrderIds, newIds, removedIds })
    saveState(driverId, currentOrderIds)
    return
  }

  // Stop any existing session first
  await stopLocationSharing()

  currentDriverId = driverId
  currentOrderIds = [...orderIds]
  running = true
  saveState(driverId, currentOrderIds)

  const isNative = Capacitor.isNativePlatform()
  console.log('[LocationService] starting watcher, isNative:', isNative)

  if (isNative) {
    try {
      const id = await BackgroundGeolocation.addWatcher(
        {
          backgroundMessage: 'Sharing your location with the student.',
          backgroundTitle: 'Deeliver · En route',
          requestPermissions: true,
          stale: false,
          distanceFilter: 5,
        },
        (location, err) => {
          if (err) {
            console.warn('[LocationService] native watcher error', err)
            return
          }
          if (!location) {
            console.log('[LocationService] native watcher tick without location')
            return
          }
          console.log('[LocationService] native location tick', {
            latitude: location.latitude,
            longitude: location.longitude,
            accuracy: location.accuracy,
            orderCount: currentOrderIds.length,
          })
          broadcast(location.latitude, location.longitude, location.accuracy, location.bearing, location.speed)
        },
      )
      nativeWatchId = id
      console.log('[LocationService] native watcher started', id)
    } catch (e: any) {
      console.error('[LocationService] native watcher failed, falling back to web:', e?.message || e)
      // Fall through to web watcher
      startWebWatcher()
    }
  } else {
    startWebWatcher()
  }
}

function startWebWatcher() {
  // Try Capacitor Geolocation first, fall back to navigator.geolocation
  try {
    Geolocation.watchPosition(
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
      (position, err) => {
        if (err || !position) {
          if (err) console.warn('[LocationService] Capacitor web watcher error', err)
          return
        }
        const { latitude, longitude, accuracy, heading, speed } = position.coords
        console.log('[LocationService] web location tick', { latitude, longitude, accuracy, orderCount: currentOrderIds.length })
        broadcast(latitude, longitude, accuracy, heading, speed)
      },
    ).then((id) => {
      webWatchId = id
      console.log('[LocationService] Capacitor web watcher started', id)
    }).catch((e) => {
      console.warn('[LocationService] Capacitor watchPosition failed, trying navigator.geolocation:', e?.message || e)
      // Final fallback: raw browser geolocation API
      if (typeof navigator !== 'undefined' && navigator.geolocation) {
        const id = navigator.geolocation.watchPosition(
          (position) => {
            const { latitude, longitude, accuracy, heading, speed } = position.coords
            console.log('[LocationService] navigator location tick', { latitude, longitude, accuracy, orderCount: currentOrderIds.length })
            broadcast(latitude, longitude, accuracy, heading, speed)
          },
          (err) => {
            console.warn('[LocationService] navigator.geolocation error', err.message)
          },
          { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 },
        )
        webWatchId = String(id)
        console.log('[LocationService] navigator.geolocation watcher started', id)
      }
    })
  } catch (e: any) {
    console.error('[LocationService] all geolocation methods failed', e?.message || e)
  }
}

/** Remove a single order from the active sharing session. If none left, stop. */
export async function removeOrderFromSharing(orderId: string) {
  currentOrderIds = currentOrderIds.filter((id) => id !== orderId)

  if (currentOrderIds.length === 0) {
    await stopLocationSharing()
  } else if (currentDriverId) {
    saveState(currentDriverId, currentOrderIds)
  }
}

/** Fully stop all location sharing. */
export async function stopLocationSharing() {
  running = false
  clearState()

  if (nativeWatchId) {
    try { await BackgroundGeolocation.removeWatcher({ id: nativeWatchId }) } catch {}
    nativeWatchId = null
  }
  if (webWatchId) {
    // Try Capacitor clearWatch first, then navigator fallback
    try { await Geolocation.clearWatch({ id: webWatchId }) } catch {}
    try { navigator.geolocation.clearWatch(Number(webWatchId)) } catch {}
    webWatchId = null
  }
  currentOrderIds = []
  currentDriverId = null
  persistedOrderRows.clear()
  lastPersistedAtByOrder.clear()
  console.log('[LocationService] stopped')
}

/** Resume sharing from persisted state (call on app boot). */
export async function resumeLocationSharing() {
  if (typeof window === 'undefined') return
  if (running) return // already active

  const state = loadState()
  if (!state || state.orderIds.length === 0) return

  console.log('[LocationService] resuming sharing for', state.orderIds.length, 'orders')
  await startLocationSharing(state.driverId, state.orderIds)
}

/** Check whether sharing is currently active. */
export function isLocationSharingActive(): boolean {
  return running
}

/** Get the current order IDs being shared. */
export function getSharingOrderIds(): string[] {
  return [...currentOrderIds]
}
